import { useCallback, useEffect, useState } from 'react';
import Animated from 'react-native-reanimated';

import { runSpring } from 'utils/animations';

const { Clock, Value, interpolate } = Animated;

/**
 * Determines the start and end positions for a given translation and state.
 *
 * @param state - Current state of the translation.
 * @param destination - Destination of the translation.
 */
function getAnimationParams(state: boolean, destination: number) {
  return {
    end: state ? 0 : destination,
    start: state ? destination : 0,
  };
}
/**
 * Get the interpolation step for a given translation node and inversion configuration.
 *
 * @param node - Animation node being tracked.
 * @param destination - Destination endpoint for the animation.
 * @param invert - If true, inverts the interpolation.
 *
 * @returns Native interpolation instruction.
 */
function getInterpolation(node: Animated.Node<number>, destination: number, invert = false) {
  const inversion = Boolean(invert);

  return interpolate(node, {
    inputRange: [0, destination],
    outputRange: [Number(!inversion), Number(inversion)],
  });
}
/**
 * React Hook for a spring animation that animates 2 values given a boolean flag
 * * true, animates translation scalar to `translateBy` and `factor` to 1
 * * false, animates translation scalar to 0, and `factor` to 0
 *
 * @param config - Configuration for initialisation of the animation
 * @param dependencies - List of values that will cause the animation to reset
 * @returns Animation tracking scalars and method to start the animation
 */
export function useBinarySpringAnimation(
  config: BinarySpringConfig,
  dependencies: unknown[]
): BinaryAnimation {
  const { invertFactor = false, translateBy, value } = config;
  const initFactor = Number(value) ^ Number(invertFactor);
  const { end, start } = getAnimationParams(value, translateBy);
  const [clock, setClock] = useState(new Clock());
  const [translation, setTranslation] = useState<Animated.Node<number>>(new Value(start));
  const [factor, setFactor] = useState<Animated.Node<number>>(new Value(initFactor));

  const animate = useCallback(
    (toValue: boolean) => {
      if (toValue === undefined || value === toValue) {
        return;
      }

      setClock(new Clock()); // reset the clock
      setFactor(getInterpolation(translation, translateBy, invertFactor));
      setTranslation(runSpring(clock, start, end));
    },
    [value, translateBy, invertFactor, translation, clock, start, end]
  );

  useEffect(() => {
    setTranslation(new Value(start));
    setFactor(new Value(initFactor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return [
    {
      factor,
      translation,
    },
    animate,
  ];
}
/* Type definitions */
type BinaryAnimation = [
  {
    /** A scalar value between 0 and 1. */
    factor: Animated.Node<number>;
    /** A scalar value between 0 and `translateBy`. */
    translation: Animated.Node<number>;
  },
  /**
   * Starts or continues the animation based on the given value.
   *
   * @param toValue - State to animate to.
   */
  (toValue: boolean) => void
];

interface BinarySpringConfig {
  /** Inverts the factor range from 1 to 0, instead of 0 to 1. */
  invertFactor?: boolean;
  /** Amount to translate by. */
  translateBy: number;
  /** Current value representing the state of the translation. */
  value: boolean;
}
